import React, { useEffect, useState } from 'react'
import { Link, useParams, useHistory } from 'react-router-dom'
import { Box, Container, Table, Group, Loader, Center } from '@mantine/core'
import { AppBadge, AppIconButton, AppInput, AppModal, AppTabs } from '../../app/common/ui'
import { AppButton, AppText, AppStack } from '../../app/common/ui'
import { format, formatDistanceToNow } from 'date-fns'
import agent from '../../app/api/agent'
import { AmmoProductDto } from '../../app/models/ammo'
import { PostDto } from '../../app/models/post'
import { ContentPhotoDto, ContentPhotoOwnerType } from '../../app/models/contentPhoto'
import { useStore } from '../../app/stores/store'
import { observer } from 'mobx-react-lite'
import { toast } from 'react-toastify'
import { AmmoScoreBadge, POST_TYPE_LABELS, POST_TYPE_COLORS } from '../../app/common/ui'
import PhotoGallery from '../builds/PhotoGallery'
import GalleryUploadButton from '../builds/GalleryUploadButton'

const VOTE_BTN = {
  background: 'var(--gs-surface2)',
  border: '1.5px solid var(--gs-border-bright)',
  color: 'var(--gs-text)'
}

const AmmoProductDetail = observer(() => {
  const { id } = useParams<{ id: string }>()
  const history = useHistory()
  const { userStore, voteStore } = useStore()
  const [product, setProduct] = useState<AmmoProductDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [reviews, setReviews] = useState<PostDto[]>([])
  const [reviewsLoading, setReviewsLoading] = useState(false)
  const [photos, setPhotos] = useState<ContentPhotoDto[]>([])
  const [photosLoading, setPhotosLoading] = useState(false)
  const [displayScore, setDisplayScore] = useState(0)
  const [displayVote, setDisplayVote] = useState<number | null>(null)
  const [rejectReason, setRejectReason] = useState('')
  const [showRejectModal, setShowRejectModal] = useState(false)

  useEffect(() => {
    agent.AmmoProducts.details(id)
      .then(p => {
        setProduct(p)
        setDisplayScore(p.voteScore ?? 0)
        setDisplayVote(p.currentUserVote ?? null)
      })
      .finally(() => setLoading(false))
  }, [id])

  useEffect(() => {
    if (!id) return
    setReviewsLoading(true)
    agent.Posts.list({ ammoProductId: id, type: 1, pageSize: 20 })
      .then(result => {
        setReviews(result.items)
      })
      .catch(() => setReviews([]))
      .finally(() => setReviewsLoading(false))
  }, [id])

  useEffect(() => {
    if (!id) return
    setPhotosLoading(true)
    agent.ContentPhotos.list(ContentPhotoOwnerType.AmmoProduct, id)
      .then(setPhotos)
      .catch(() => setPhotos([]))
      .finally(() => setPhotosLoading(false))
  }, [id])

  const handleApprove = async () => {
    if (!product) return
    try {
      await agent.AmmoProducts.approve(product.id)
      toast.success('Ammo product approved')
      setProduct((prev) => (prev ? { ...prev, isActive: true } : null))
    } catch (error) {
      toast.error('Failed to approve')
    }
  }

  const handleReject = async () => {
    if (!product) return
    try {
      await agent.AmmoProducts.reject(product.id, rejectReason)
      toast.success('Ammo product rejected')
      setShowRejectModal(false)
      setProduct((prev) => (prev ? { ...prev, isActive: false } : null))
    } catch (error) {
      toast.error('Failed to reject')
    }
  }

  const handleVote = async (value: number) => {
    if (!product || !id) return
    if (!userStore.isLoggedIn) {
      history.push('/register')
      return
    }
    const prevScore = displayScore
    const prevVote = displayVote
    const delta = value === displayVote ? -value : value - (displayVote ?? 0)
    setDisplayScore((s) => s + delta)
    setDisplayVote(value === displayVote ? null : value)
    try {
      if (value === displayVote) {
        await voteStore.removeVote(7, id)
      } else {
        await voteStore.castVote(7, id, value)
      }
    } catch {
      setDisplayScore(prevScore)
      setDisplayVote(prevVote)
    }
  }

  const handleGalleryDelete = async (photoId: string) => {
    await agent.ContentPhotos.delete(photoId)
    setPhotos((prev) => prev.filter((p) => p.id !== photoId))
  }

  const handleGallerySetMain = async (photoId: string) => {
    await agent.ContentPhotos.setMain(photoId)
    if (id) {
      const list = await agent.ContentPhotos.list(ContentPhotoOwnerType.AmmoProduct, id)
      setPhotos(list)
    }
  }

  const handleGalleryUploadSuccess = (photo: ContentPhotoDto) => {
    setPhotos((prev) => [...prev, photo])
  }

  if (loading) {
    return (
      <Center>
        <Loader />
      </Center>
    )
  }

  if (!product) { return <AppText>Product not found</AppText> }

  return (
    <Box sx={{ background: 'var(--gs-bg)', minHeight: '100vh', paddingTop: '24px', paddingBottom: '24px' }}>
      <Container size="md">
        <AppStack spacing="md">
          {/* Back Link */}
          <AppButton
            variant="subtle"
            onClick={() => history.push('/ammo')}
            sx={{ width: 'fit-content', paddingLeft: 0 }}
          >
            ← Back to Ammo Registry
          </AppButton>

          {/* Header */}
          <Box>
            <AppText
              sx={{
                fontSize: '28px',
                fontWeight: 700,
                marginBottom: '4px'
              }}
            >
              {product.brand} {product.name}
            </AppText>
            <AppText
              sx={{
                color: 'var(--gs-muted)',
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: '14px'
              }}
            >
              {product.cartridgeSpecName}
            </AppText>
          </Box>

          {/* Spec Table */}
          <Box sx={{ border: '1px solid var(--gs-border)', borderRadius: '6px', overflow: 'hidden' }}>
            <Table>
              <tbody>
                <tr style={{ borderBottom: '1px solid var(--gs-border)' }}>
                  <td style={{ padding: '12px', background: 'var(--gs-surface)', fontWeight: 700, width: '200px' }}>
                    Bullet Weight
                  </td>
                  <td style={{ padding: '12px', fontFamily: 'JetBrains Mono, monospace' }}>
                    {product.grainWeight ? `${product.grainWeight}gr` : '—'}
                  </td>
                </tr>
                <tr style={{ borderBottom: '1px solid var(--gs-border)' }}>
                  <td style={{ padding: '12px', background: 'var(--gs-surface)', fontWeight: 700 }}>
                    Bullet Type
                  </td>
                  <td style={{ padding: '12px', fontFamily: 'JetBrains Mono, monospace' }}>
                    {product.bulletType}
                  </td>
                </tr>
              </tbody>
            </Table>
          </Box>

          {/* Compatible Firearms Link */}
          <Box
            sx={{
              padding: '12px',
              background: 'var(--gs-surface)',
              border: '1px solid var(--gs-border)',
              borderRadius: '6px',
              cursor: 'pointer',
              '&:hover': { borderColor: '#1A3A5C' }
            }}
            onClick={() => history.push(`/ammo/specs/${product.cartridgeSpecId}`)}
          >
            <AppText size="sm">
              This is a {product.cartridgeSpecName} load.{' '}
              <AppText
                component="span"
                sx={{ color: 'var(--gs-accent)', fontWeight: 500 }}
              >
                View compatible firearms →
              </AppText>
            </AppText>
          </Box>

          {/* Voting Section */}
          <Group spacing="xs">
            <AppIconButton
              size="md"
              variant="subtle"
              sx={{ ...VOTE_BTN, ...(displayVote === 1 ? { borderColor: 'var(--gs-accent)', color: 'var(--gs-accent)' } : {}) }}
              onClick={() => handleVote(1)}
              aria-label="Upvote"
            >
              <AppText weight={800} size="md" sx={{ lineHeight: 1 }}>
                +
              </AppText>
            </AppIconButton>
            <AppText sx={{ minWidth: 28, textAlign: 'center', fontWeight: 600 }}>{displayScore}</AppText>
            <AppIconButton
              size="md"
              variant="subtle"
              sx={{ ...VOTE_BTN, ...(displayVote === -1 ? { borderColor: 'var(--gs-accent)', color: 'var(--gs-accent)' } : {}) }}
              onClick={() => handleVote(-1)}
              aria-label="Downvote"
            >
              <AppText weight={800} size="md" sx={{ lineHeight: 1 }}>
                −
              </AppText>
            </AppIconButton>
          </Group>

          {/* Admin Panel */}
          {userStore.user?.isAdmin && (
            <Box
              sx={{
                padding: '16px',
                background: 'var(--gs-surface2)',
                border: '1px solid var(--gs-border)',
                borderRadius: '6px'
              }}
            >
              <AppText weight={700} size="sm" sx={{ marginBottom: '12px' }}>
                Admin Actions
              </AppText>
              <Group spacing="sm">
                <AppButton size="sm" onClick={handleApprove} disabled={product.isActive}>
                  Approve
                </AppButton>
                <AppButton
                  size="sm"
                  color="red"
                  onClick={() => setShowRejectModal(true)}
                  disabled={!product.isActive}
                >
                  Reject
                </AppButton>
              </Group>
            </Box>
          )}

          {/* Community Photo Gallery */}
          {id && (
            <Box>
              <Group position="apart" align="center" mb="sm" sx={{ flexWrap: 'wrap' }}>
                <AppText sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 600, fontSize: 14, color: 'var(--gs-text)' }}>
                  COMMUNITY PHOTOS
                </AppText>
                {userStore.isLoggedIn && (
                  <GalleryUploadButton
                    ownerType={ContentPhotoOwnerType.AmmoProduct}
                    ownerId={id}
                    canUpload={true}
                    onSuccess={handleGalleryUploadSuccess}
                    label="+ Add Photo"
                  />
                )}
              </Group>
              {photosLoading
                ? (
                <Center py="lg">
                  <Loader size="sm" />
                </Center>
                  )
                : photos.length > 0
                  ? (
                <PhotoGallery
                  variant="ammoProduct"
                  photos={photos}
                  canManage={userStore.user?.isAdmin}
                  onDelete={handleGalleryDelete}
                  onSetMain={handleGallerySetMain}
                />
                    )
                  : (
                <Box
                  sx={{
                    padding: '24px',
                    textAlign: 'center',
                    border: '1px dashed var(--gs-border)',
                    borderRadius: '6px'
                  }}
                >
                  <AppText sx={{ color: 'var(--gs-muted)' }}>
                    No photos yet. Be the first to share!
                  </AppText>
                </Box>
                    )}
            </Box>
          )}

          {/* Reviews Section */}
          <Box>
            <AppText weight={700} size="lg" sx={{ marginBottom: '12px' }}>
              Community Reviews
            </AppText>
            {reviewsLoading
              ? (
              <Center py="lg">
                <Loader size="sm" />
              </Center>
                )
              : reviews.length > 0
                ? (
              <AppStack spacing="md">
                {reviews.map((review) => (
                  <Box
                    key={review.id}
                    component={Link}
                    to={`/blog/${review.id}`}
                    sx={{
                      padding: '16px',
                      border: '1px solid var(--gs-border)',
                      borderRadius: '6px',
                      background: 'var(--gs-card)',
                      textDecoration: 'none',
                      transition: 'border-color 0.15s',
                      '&:hover': { borderColor: 'var(--gs-accent)' }
                    }}
                  >
                    <Group align="flex-start" spacing="sm" noWrap position="apart" mb="xs">
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <AppText
                          size="sm"
                          weight={600}
                          sx={{ color: 'var(--gs-text)' }}
                          lineClamp={2}
                        >
                          {review.title}
                        </AppText>
                        <Group spacing="xs" mt="xs">
                          <AppText component={Link} to={`/profiles/${review.authorUsername}`} size="xs" weight={500} sx={{ color: 'var(--gs-accent)' }}>
                            {review.authorDisplayName || review.authorUsername}
                          </AppText>
                          <AmmoScoreBadge tier={review.authorAmmoTier ?? 'Newcomer'} score={review.authorAmmoScore ?? 0} size="xs" />
                          <AppText size="xs" color="dimmed">
                            {formatDistanceToNow(new Date(review.createdAt), { addSuffix: true })}
                          </AppText>
                        </Group>
                      </Box>
                      {review.rating && (
                        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                          <AppText size="sm" weight={700} sx={{ color: 'var(--gs-accent)' }}>
                            {review.rating}/5
                          </AppText>
                        </Box>
                      )}
                    </Group>
                    {review.excerpt && (
                      <AppText size="sm" color="dimmed" lineClamp={2} mt="xs">
                        {review.excerpt}
                      </AppText>
                    )}
                  </Box>
                ))}
              </AppStack>
                  )
                : (
              <Box
                sx={{
                  padding: '24px',
                  textAlign: 'center',
                  border: '1px dashed var(--gs-border)',
                  borderRadius: '6px'
                }}
              >
                <AppText sx={{ color: 'var(--gs-muted)' }}>
                  No reviews yet. Be the first to share your experience!
                </AppText>
              </Box>
                  )}
          </Box>
        </AppStack>
      </Container>

      {/* Reject Modal */}
      <AppModal
        opened={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        title="Reject Ammo Product"
      >
        <AppStack spacing="md">
          <AppInput
            label="Reason (optional)"
            placeholder="Why is this being rejected?"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.currentTarget.value)}
          />
          <Group position="right">
            <AppButton variant="subtle" onClick={() => setShowRejectModal(false)}>
              Cancel
            </AppButton>
            <AppButton color="red" onClick={handleReject}>
              Reject
            </AppButton>
          </Group>
        </AppStack>
      </AppModal>
    </Box>
  )
})

export default AmmoProductDetail
